import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import {
  HINTED_QUERIES, hintTopic, nextBackoff, nextFlushDelay, queriesForHint,
} from "@/lib/acd";
import { streamEvents } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useKeys } from "@/lib/queries";

/**
 * The console's one subscription to `GET /events` (ADR-018): refetch hints, not data.
 *
 * The event-tracker publishes a small envelope after every state change a screen shows — a rep's
 * ACD state, a registration, the dialer's offer — and core-api forwards the channels this person
 * may watch. Each hint refetches the queries that read that state (`queriesForHint`), so a missed
 * one costs a stale second rather than a wrong screen, and the queries still poll slowly as a
 * backstop for a stream that is down.
 *
 * Mounted once, in the app shell, for as long as somebody is signed in. The stream is a fetch
 * rather than an `EventSource` for the reason `streamEvents` gives. A stream that ends is reopened
 * after a backoff, and everything a hint could have refreshed is refetched straight away, because
 * the hints it missed while it was down are not coming back.
 *
 * Hints come in bursts — one call moves a caller, a rep and a line within milliseconds — so they are
 * collected and refetched together once the burst settles (`nextFlushDelay`). Refetching on each
 * one cancelled the refetch the hint before had started, and on a busy floor a query could go on
 * being cancelled for as long as the calls kept coming.
 */
export function LiveHints() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const keys = useKeys();
  const userId = user?.id;

  React.useEffect(() => {
    if (userId === undefined) return;
    let stream: { close: () => void } | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let backoff: number | null = null;
    let closed = false;
    // The query keys this burst of hints has made stale, by their joined name, and when it began.
    const stale = new Map<string, readonly string[]>();
    let burstStartedAt = 0;
    let flushTimer: ReturnType<typeof setTimeout> | undefined;

    const flush = () => {
      flushTimer = undefined;
      for (const queryKey of stale.values()) void queryClient.invalidateQueries({ queryKey: keys.scoped(queryKey) });
      stale.clear();
    };

    const open = () => {
      stream = streamEvents("/events", {
        onData: (data) => {
          backoff = null;
          const topic = hintTopic(data);
          if (!topic) return;
          for (const queryKey of queriesForHint(topic)) stale.set(queryKey.join("/"), queryKey);
          if (stale.size === 0) return;
          const now = Date.now();
          if (flushTimer === undefined) burstStartedAt = now;
          clearTimeout(flushTimer);
          flushTimer = setTimeout(flush, nextFlushDelay(burstStartedAt, now));
        },
        onEnd: () => {
          if (closed) return;
          for (const queryKey of HINTED_QUERIES) void queryClient.invalidateQueries({ queryKey: keys.scoped(queryKey) });
          backoff = nextBackoff(backoff);
          timer = setTimeout(open, backoff);
        },
      });
    };
    open();

    return () => {
      closed = true;
      clearTimeout(timer);
      clearTimeout(flushTimer);
      stream?.close();
    };
  }, [userId, queryClient, keys]);

  return null;
}
